import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Clock, Calendar, BookOpen, ExternalLink } from "lucide-react";

/**
 * Reusable long-form research article layout.
 *
 * Props:
 *   title       — string, article title
 *   category    — string, small uppercase eyebrow ("Gastrointestinal", "Tendon Research", etc.)
 *   readTime    — string, "12 min read"
 *   publishedAt — string, "Feb 2026"
 *   sections    — array of { id, label } used for the sidebar table of contents.
 *                 Each id must match an anchor id inside the article body.
 *   references  — array of { n, text, url } — numbered references list at the bottom.
 *   notice      — OPTIONAL { eyebrow, body } to override the default Research Use Only
 *                 callout copy. When omitted, the standard RUO copy is used.
 *   children    — the article body (headings, paragraphs).
 *
 * Renders a full-width dark-themed article page with a sticky sidebar TOC on
 * desktop, a prominent Research Use Only callout near the top, and a
 * clickable reference list. Match the Quantum Genesis Labs aesthetic exactly.
 */
export const ArticleLayout = ({
    title,
    category,
    readTime,
    publishedAt,
    sections = [],
    references = [],
    notice,
    children,
}) => {
    const [activeId, setActiveId] = useState(sections[0]?.id || "");

    // Smooth-scroll to a section when the sidebar link is tapped.
    const scrollTo = (id) => (e) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top, behavior: "smooth" });
        setActiveId(id);
    };

    // Update the highlighted TOC entry as the reader scrolls.
    useEffect(() => {
        if (sections.length === 0) return;
        const onScroll = () => {
            const y = window.scrollY + 140;
            let current = sections[0].id;
            for (const s of sections) {
                const el = document.getElementById(s.id);
                if (el && el.offsetTop <= y) current = s.id;
            }
            setActiveId(current);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [sections]);

    return (
        <article className="relative" data-testid="article-layout">
            <div className="absolute inset-x-0 top-0 h-[320px] overflow-hidden -z-10">
                <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.08] via-violet-500/[0.04] to-[#05050A]" />
            </div>

            <div className="max-w-6xl mx-auto px-5 lg:px-10 pt-10 pb-24">
                {/* Back link */}
                <Link
                    to="/research"
                    data-testid="article-back-link"
                    className="inline-flex items-center gap-1.5 text-[13px] text-slate-400 hover:text-indigo-300 transition-colors mb-8"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to Research Library
                </Link>

                {/* Header */}
                <header className="max-w-3xl">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-indigo-400 mb-3">
                        {category}
                    </p>
                    <h1
                        data-testid="article-title"
                        className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tighter leading-[1.15] text-white"
                    >
                        {title}
                    </h1>
                    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-slate-500">
                        {publishedAt && (
                            <span className="inline-flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" /> {publishedAt}
                            </span>
                        )}
                        {readTime && (
                            <span className="inline-flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5" /> {readTime}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                            <BookOpen className="w-3.5 h-3.5" /> Educational
                        </span>
                    </div>
                </header>

                {/* RUO notice */}
                <div
                    data-testid="article-ruo-notice"
                    className="mt-10 rounded-2xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/[0.06] to-violet-500/[0.04] px-5 sm:px-7 py-5 sm:py-6 max-w-3xl"
                >
                    <div className="flex items-start gap-3.5">
                        <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center shrink-0">
                            <ShieldAlert className="w-4 h-4 text-indigo-300" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-indigo-300 mb-1.5">
                                {notice?.eyebrow || "Research Use Only"}
                            </p>
                            <p className="text-[13.5px] sm:text-sm text-slate-300 leading-[1.7]">
                                {notice?.body || (
                                    <>
                                        This article summarizes published scientific research and is provided
                                        for informational purposes only. Findings from laboratory or animal
                                        studies should not be interpreted as evidence of safety or
                                        effectiveness in humans.
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body + sticky TOC */}
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-14">
                    <div
                        data-testid="article-body"
                        className="article-prose max-w-3xl text-slate-300 leading-[1.85] text-[15px] sm:text-[15.5px]"
                    >
                        {children}

                        {references.length > 0 && (
                            <section id="references" className="mt-16 pt-10 border-t border-white/10">
                                <h2 className="text-xl sm:text-2xl font-light tracking-tight text-white mb-6">
                                    References
                                </h2>
                                <ol
                                    data-testid="article-references"
                                    className="space-y-3 text-[13.5px] text-slate-400 leading-relaxed list-none pl-0"
                                >
                                    {references.map((r) => (
                                        <li key={r.n} id={`ref-${r.n}`} className="flex gap-3">
                                            <span className="text-indigo-400/80 shrink-0 tabular-nums">{r.n}.</span>
                                            <span>
                                                {r.text}{" "}
                                                {r.url && (
                                                    <a
                                                        href={r.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`article-ref-link-${r.n}`}
                                                        className="inline-flex items-center gap-1 text-indigo-300/90 hover:text-indigo-200 underline-offset-2 hover:underline transition-colors"
                                                    >
                                                        View source <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            </section>
                        )}
                    </div>

                    {/* Sticky TOC (desktop only) */}
                    {sections.length > 0 && (
                        <aside className="hidden lg:block">
                            <nav
                                data-testid="article-toc"
                                className="sticky top-24 rounded-2xl border border-white/10 bg-[#0A0A12]/60 p-5"
                                aria-label="Article sections"
                            >
                                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-4">
                                    On this page
                                </p>
                                <ul className="space-y-2">
                                    {sections.map((s) => (
                                        <li key={s.id}>
                                            <a
                                                href={`#${s.id}`}
                                                onClick={scrollTo(s.id)}
                                                data-testid={`article-toc-${s.id}`}
                                                className={`block text-[12.5px] leading-snug tracking-tight transition-colors ${
                                                    activeId === s.id
                                                        ? "text-indigo-300"
                                                        : "text-slate-400 hover:text-white"
                                                }`}
                                            >
                                                {s.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </aside>
                    )}
                </div>
            </div>
        </article>
    );
};

/** H2 helper — use as <ArticleH2 id="foo">Heading</ArticleH2>. */
export const ArticleH2 = ({ id, children }) => (
    <h2
        id={id}
        className="text-2xl sm:text-[26px] font-light tracking-tight text-white mt-14 mb-4 scroll-mt-24"
    >
        {children}
    </h2>
);

/** Inline superscript citation marker: <Cite n={1} /> — links to #ref-1. */
export const Cite = ({ n }) => (
    <sup>
        <a
            href={`#ref-${n}`}
            data-testid={`article-cite-${n}`}
            className="ml-0.5 text-[11px] text-indigo-300 hover:text-indigo-200 no-underline hover:underline"
        >
            [{n}]
        </a>
    </sup>
);

export default ArticleLayout;
